<template>
  <div class="bubble-chart">
    <div :id="elId"></div>
    <bubble-chart-tooltip
      v-model="tooltip.show"
      ref="tooltip"
      :top="tooltip.top"
      :left="tooltip.left"
      :text="tooltip.text"
    />
  </div>
</template>

<script>
import { max, scalePoint, scaleSqrt, select, axisTop, axisLeft } from 'd3';
import BubbleChartTooltip from './BubbleChartTooltip.vue';

export default {
  name: 'Bubble',
  components: {
    BubbleChartTooltip,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    years: {
      type: Array,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
    tab: {
      type: String,
      required: true,
      default: 'New FinTech firms by year',
    },
    category: {
      type: String,
      required: true,
      default: 'Total Firms',
    },
    showCount: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    elId: 'bubble-viz',
    charts: {
      svg: null,
      g: null,
      x: null,
      y: null,
      gBubbles: null,
      bubbles: null,
    },
    tooltip: {
      text: '',
      show: false,
      top: 0,
      left: 0,
    },
    width: 0, // Get the size from this.getWidthSize()
    height: 400,
  }),
  computed: {
    xScale() {
      return scalePoint().rangeRound([0, this.chartSettings.width]).padding(0.5).domain(this.years);
    },
    yScale() {
      return scalePoint().rangeRound([this.chartSettings.height, 0]).padding(0.5).domain(this.countries);
    },
    xAxis() {
      return axisTop().tickSize(0).scale(this.xScale);
    },
    yAxis() {
      return axisLeft().tickSize(0).scale(this.yScale);
    },
    rScale() {
      const domainMin = 0;

      const domainMax = max(this.data, (d) => {
        if (this.tab === 'New FinTech firms by year') {
          return +d['New Firms'];
        }
        // Cumulative
        return +d.Operating;
      });

      const rScale = scaleSqrt().domain([domainMin, domainMax]);

      if (this.$breakpoint.mobile) {
        return rScale.range([0, 20]);
      }
      return rScale.range([0, 30]);
    },
    chartMargin() {
      return { top: 16, right: 0, bottom: 16, left: 70 };
    },
    chartSettings() {
      if (this.$breakpoint.mobile) {
        return {
          width: this.width - this.chartMargin.left - this.chartMargin.right,
          height: this.height - this.chartMargin.top - this.chartMargin.bottom,
        };
      }
      return {
        width: this.width - this.chartMargin.left - this.chartMargin.right,
        height: this.height - this.chartMargin.top - this.chartMargin.bottom,
      };
    },
  },
  watch: {
    tab() {
      this.updateBubbles();
    },
    category() {
      this.updateBubbles();
    },
    showCount() {
      this.updateBubbles();
    },
  },
  methods: {
    init() {
      // Get width size
      this.width = this.getWidthSize();

      // Props
      const { width, height } = this; // in px
      let { svg, g, x, y, gBubbles } = this.charts;

      // Main SVG
      svg = select(`#${this.elId}`).append('svg').style('width', width).style('height', height).call(this.responsivefy);

      // The chart container
      g = svg
        .append('g')
        .attr('class', 'chart-container')
        .attr('transform', `translate(${this.chartMargin.left}, ${this.chartMargin.bottom})`);

      // X AXIS
      x = g.append('g').attr('class', 'axis axis__x').call(this.xAxis);
      x.select('.domain').remove();
      x.selectAll('.tick text').attr('class', 'axis__x-text');
      // Y AXIS
      y = g
        .append('g')
        .attr('class', 'axis axis__y')
        .style('text-anchor', 'start')
        .style('transform', 'translate(-60px, 0)')
        .call(this.yAxis);
      y.select('.domain').remove();
      y.selectAll('.tick text').attr('class', 'axis__y-text');

      // Bubbles container
      gBubbles = g.append('g').attr('class', 'bubbles-container');

      // Bubble wrapper
      this.charts.bubbles = gBubbles
        .selectAll('.bubble')
        .data(this.data)
        .enter()
        .append('g')
        .attr('class', (d) => `bubble ${d.id}`)
        .attr('transform', (d) => `translate(${this.xScale(d.Year)}, ${this.yScale(d.Country)})`)
        .on('mousemove.bubble', this.handleTooltip)
        .on('mouseenter.bubble', this.handleTooltip)
        .on('mouseleave.bubble', this.removeTooltip);

      // Bubble circle
      this.charts.bubbles
        .append('circle')
        .attr('class', (d) => `bubble-circle ${d.id}-circle`)
        .attr('r', (d) => {
          if (this.tab === 'New FinTech firms by year') {
            return this.rScale(+d['New Firms']);
          }
          // Cumulative
          return this.rScale(+d.Operating);
        })
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('fill', (d) => {
          switch (d.Country) {
            case 'Vietnam':
              return '#F09252';
            case 'Thailand':
              return '#F6C76A';
            case 'Singapore':
              return '#005EB8';
            case 'Philippines':
              return '#A295E2';
            case 'Malaysia':
              return '#FDC4D0';
            case 'Indonesia':
              return '#E25A5F';
            case 'Myanmar':
              return '#F7A48C';
            case 'Cambodia':
              return '#A9A8A9';

            default:
              return '#000000';
          }
        })
        .style('opacity', () => {
          if (this.showCount) return 0.2;
          return 1;
        });

      // Bubble text
      this.charts.bubbles
        .append('text')
        .attr('class', (d) => `bubble-text ${d.id}-text`)
        .style('text-anchor', 'middle')
        // Adjust the center a bit
        .attr('dy', 4.5)
        .text((d) => {
          if (this.tab === 'New FinTech firms by year') {
            return d['New Firms'];
          }
          // Cumulative
          return d.Operating;
        })
        .style('opacity', () => {
          return this.showCount ? 1 : 0;
        });

      // Redraw on window changes
      select(window).on('resize.test', this.redraw);
    },
    responsivefy(svg) {
      // container will be the DOM element that the svg is appended to
      // we then measure the container and find its aspect ratio
      const { width, height } = this;
      const aspect = width / height;

      // set viewBox attribute to the initial size
      // control scaling with preserveAspectRatio
      // resize svg on inital page load
      svg.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMinYMid').call(this.resize, aspect);

      // add a listener so the chart will be resized
      // when the window resizes
      // multiple listeners for the same event type
      // requires a namespace, i.e., 'click.foo'
      // api docs: https://goo.gl/F3ZCFr
      select(window).on('resize.test', () => {
        this.resize(svg, aspect);
      });
    },
    resize(svg, aspect) {
      // this is the code that resizes the chart
      // it will be called on load
      // and in response to window resizes gets the width of the container
      // and resizes the svg to fill it while maintaining a consistent aspect ratio
      const { width } = this;

      svg.attr('width', width);
      svg.attr('height', Math.round(width / aspect));
    },
    getWidthSize() {
      return document.getElementById(this.elId).offsetWidth;
    },
    getHeightSize() {
      return document.getElementById(this.elId).offsetHeight;
    },
    formatData() {
      this.data = this.rawData
        .filter((d) => this.category === d.Category)
        .map((val) => {
          return {
            id: `${val.Country}-${val.Year}`.replace(/ /g, ''),
            ...val,
          };
        });
    },
    updateBubbles() {
      this.charts.bubbles.data(this.data).each((d) => {
        // Update circles
        this.charts.bubbles
          .select(`.${d.id}-circle`)
          .transition()
          .duration(500)
          .attr('r', () => {
            if (this.tab === 'New FinTech firms by year') {
              return this.rScale(+d['New Firms']);
            }
            // Cumulative
            return this.rScale(+d.Operating);
          })
          .style('opacity', () => {
            if (this.showCount) return 0.2;
            return 1;
          });

        // Update texts
        this.charts.bubbles
          .select(`.${d.id}-text`)
          .transition()
          .duration(500)
          .text(() => {
            if (this.tab === 'New FinTech firms by year') {
              return d['New Firms'];
            }
            // Cumulative
            return d.Operating;
          })
          .style('opacity', () => {
            return this.showCount ? 1 : 0;
          });
      });
    },
    handleTooltip(e, d) {
      const el = select(e.target);
      const circle = el.select('circle');

      el.style('cursor', 'pointer');
      circle.style('stroke', '#121212').style('stroke-width', '2px');

      this.renderTooltip(d, e);
    },
    removeTooltip(e) {
      const el = select(e.target);
      const circle = el.select('circle');

      el.style('cursor', 'inherit');

      circle.style('stroke', 'transparent');

      this.tooltip.show = false;
    },
    renderTooltip(d, e) {
      const mouseXPadding = 20;
      const mouseYPadding = 20;

      // Set tooltip correct position, make sure it doesn't go offside
      const posX = e.layerX;
      const posY = e.layerY;
      let tooltipLeft = posX + mouseXPadding;
      let tooltipTop = posY + mouseYPadding;

      this.tooltip.text = this.renderTooltipText(d);

      const tooltipEl = this.$refs.tooltip.$el;
      const tooltipWidth = tooltipEl.getBoundingClientRect().width;
      const tooltipHeight = tooltipEl.getBoundingClientRect().height;

      // Offisde the x1
      if (tooltipLeft + tooltipWidth >= this.width) {
        tooltipLeft = posX - tooltipWidth - mouseXPadding;
      }
      // Offside the y1
      if (tooltipTop + tooltipHeight >= this.height) {
        tooltipTop = posY - tooltipHeight - mouseYPadding;
      }
      // Offside the x0
      if (tooltipLeft < 0) {
        tooltipLeft = posX + mouseXPadding;
      }
      // Offside the y0
      if (tooltipTop < 0) {
        tooltipTop = posY + mouseYPadding;
      }

      this.tooltip.left = tooltipLeft;
      this.tooltip.top = tooltipTop;
      this.tooltip.show = true;
    },
    renderTooltipText(d) {
      let text = `<span class="tooltip-text__p">Year: ${d.Year}</span><br/>
        <span class="tooltip-text__p">Country: ${d.Country}</span><br>`;

      if (this.tab === 'New FinTech firms by year') {
        text += `<span class="tooltip-text__p-last">Total companies: ${d['New Firms']}</span>`;
      } else {
        // Cumulative
        text += `<span class="tooltip-text__p-last">Total companies: ${d.Operating}</span>`;
      }

      return text;
    },
    redraw() {
      // Redraw svg
      const container = select(`#${this.elId}`);
      container.node().removeChild(container.select('svg').node());
      this.init();
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss">
.axis {
  font-family: 'Noto Sans Regular', sans-serif;
  font-size: 14.5px;
  color: #121212;
  line-height: 20px;

  &__y-text {
    font-weight: 500;
  }

  @media #{map-get($media-breakpoints, 'mobile')} {
    font-size: 13px;

    &__y-text {
      font-weight: 600;
      line-height: 20px;
    }

    &__x-text {
      line-height: 18.9px;
    }
  }
}
.bubble-text {
  font-size: 14px;
  fill: #005EB8;
  line-height: 21.6px;
  font-weight: 500;

  @media #{map-get($media-breakpoints, 'mobile')} {
    font-size: 14px;
    line-height: 20px;
  }
}
</style>
